import { NativeModules, Platform } from 'react-native';
import { initAndGetAnalyticsSdk } from '@mmt/event-logger';
import { RisBaseEvent } from './RIS/RisPdtV2Types';
import { getDataFromAsynStorage, setDataToAsyncStorage } from '../../Utils/RailsConstant';
import { EXPERIMENT_DETAILS_CONTEXT, PDT_JOURNEY_ID_KEY } from './CommonPdtConstants';
import { generateUUID } from '@mmt/navigation/src/util';
import { logGrafanaErrorMetrics } from 'apps/rails/src/Utils/RailsGrafanaTracker';
import { ENTITY_TYPE, PAGE, NEW_PDT_ERROR_CODES } from 'apps/rails/src/types/grafanaTracking.types';
import { ENTITY } from 'apps/rails/src/Utils/RailsConstant';
import { getLocusIdFromStationDetails } from 'apps/rails/src/pages/RailsLandingPage/Store/RailsLandingPageActions';

const AnalyticsSdk = initAndGetAnalyticsSdk({});

export const logRailsEventToPDT = (arg: RisBaseEvent | any) => {
  try {
    const pdtObj = {
      ...arg,
      ...(EXPERIMENT_DETAILS_CONTEXT() || {}),
      ...(Array.isArray(arg.addon_details) && { addon_details: stripAddonProbability(arg.addon_details) }),
    };
    // Remove platform check - PDT should work on both web and mobile
    _logEventToPDT(pdtObj);
  } catch (e: any) {
    console.error('Error while PDT log', e);
    logGrafanaErrorMetrics(
      PAGE.ANY,
      ENTITY_TYPE.PAGE,
      ENTITY.GENERIC_PDT_MEASUREMENT_PLATFORM_ERROR,
      e,
      NEW_PDT_ERROR_CODES.TOP_LEVEL,
    );
  }
};

const _logEventToPDT = async (loggerObj: RisBaseEvent | any) => {
  try {
    if (Platform.OS === 'web') {
      if (!AnalyticsSdk) {
        return;
      }
      console.log('Rails Pdt log event:-', loggerObj);
      AnalyticsSdk.track(loggerObj);
    } else {
      const { PdtV2Module } = NativeModules;
      if (!PdtV2Module?.trackEvent) {
        return;
      }
      console.log('Rails Pdt log event:-', loggerObj);
      await PdtV2Module.trackEvent({ payload: JSON.stringify(loggerObj) });
    }
  }
  catch (error: any) {
    logGrafanaErrorMetrics(
      PAGE.ANY,
      ENTITY_TYPE.PAGE,
      ENTITY.GENERIC_PDT_MEASUREMENT_PLATFORM_ERROR,
      error,
      NEW_PDT_ERROR_CODES.GENERIC,
    );
  }
};

export const getTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const getJourneyId = async (searchContext: any) => {
  const { originStation = {}, destinationStation = {}, departureDate = '' } = searchContext;
  const newSearchContextId = `${originStation.code}-${destinationStation.code}-${departureDate}`;
  const { prevSearchContextId = '', journeyId = '' } =
    (await getDataFromAsynStorage(PDT_JOURNEY_ID_KEY)) || {};
  let newJourneyId = journeyId;
  if (newSearchContextId !== prevSearchContextId) {
    newJourneyId = generateUUID();
    await setDataToAsyncStorage(PDT_JOURNEY_ID_KEY, {
      prevSearchContextId: newSearchContextId,
      journeyId: newJourneyId,
    });
  }
  return {
    journey_id: newJourneyId,
  };
};

const enrichStation = (station: any, locusFallback: any) => ({
  ...station,
  locusCode: station?.locusCode || locusFallback?.locusCode || '',
  locusType: station?.locusType || locusFallback?.locusType || '',
  locusV2Id: station?.locusV2Id || locusFallback?.locusV2Id || '',
  locusV2Type: station?.locusV2Type || locusFallback?.locusV2Type || '',
});


export const buildEnrichedSearchContext = async (state: any) => {
  const { originStation, destinationStation, departureDate } = state?.railsListing || {};
  const locusDetails = state?.railsLanding?.LocusDetails || {};
  const selectedTravelers = state?.railsTraveler?.selectedTravelers || [];

  const fromLocusCode = originStation?.locusCode || locusDetails?.fromLocusCode;
  const toLocusCode = destinationStation?.locusCode || locusDetails?.toLocusCode;

  let fetchedLocusData: any = {};
  if ((!fromLocusCode || !toLocusCode) && originStation?.code && destinationStation?.code) {
    try {
      fetchedLocusData = (await getLocusIdFromStationDetails({
        from_station_code: originStation.code,
        to_station_code: destinationStation.code,
      })) || {};
    } catch (_err) {
      console.error('buildEnrichedSearchContext: failed to fetch locus data', _err);
    }
  }

  return {
    originStation: enrichStation(originStation, {
      locusCode: locusDetails?.fromLocusCode || fetchedLocusData?.fromLocusCode,
      locusType: locusDetails?.sourceLocusType || fetchedLocusData?.sourceLocusType,
      locusV2Id: locusDetails?.sourceLocusV2Id || fetchedLocusData?.sourceLocusV2Id,
      locusV2Type: locusDetails?.sourceLocusV2Type || fetchedLocusData?.sourceLocusV2Type,
    }),
    destinationStation: enrichStation(destinationStation, {
      locusCode: locusDetails?.toLocusCode || fetchedLocusData?.toLocusCode,
      locusType: locusDetails?.destinationLocusType || fetchedLocusData?.destinationLocusType,
      locusV2Id: locusDetails?.destinationLocusV2Id || fetchedLocusData?.destinationLocusV2Id,
      locusV2Type: locusDetails?.destinationLocusV2Type || fetchedLocusData?.destinationLocusV2Type,
    }),
    departureDate,
    pax: {
      adult: Math.max(selectedTravelers.length, 1),
      child: { count: 0, ages: [] },
      infant: 0,
    },
  };
};

export const stripAddonProbability = (addonDetails: any[]) => {
  if (!Array.isArray(addonDetails)) return addonDetails;
  return addonDetails.map(({ probablity, probability, confirmation_probablity, ...rest }: any) => rest);
};

const transformAddonDetailsForXDM = (addonDetails: any[]) => {
  if (!Array.isArray(addonDetails)) return addonDetails;
  return addonDetails.map(({ probablity, probability, confirmation_probablity, ...addon }: any) => ({
    ...addon,
    ...(addon.variant !== undefined && addon.variant !== null
      ? { variant: String(addon.variant) }
      : {}),
    selected: typeof addon.selected === 'boolean' ? addon.selected : false,
    is_preselected: typeof addon.is_preselected === 'boolean' ? addon.is_preselected : false,
  }));
};

const parseDurationToMinutes = (duration: any): number | null => {
  if (duration === null || duration === undefined) return null;
  if (typeof duration === 'number') return duration;
  const num = Number(duration);
  if (!isNaN(num)) return num;
  const match = String(duration).match(/(\d+)\s*[hH]\s*(\d+)?\s*[mM]?/);
  if (match) return parseInt(match[1], 10) * 60 + (parseInt(match[2], 10) || 0);
  return null;
};

export const transformPayloadForXDM = (payload: any) => {
  if (!payload || typeof payload !== 'object') return payload;
  const result = { ...payload };

  if (Array.isArray(result.addon_details)) {
    result.addon_details = transformAddonDetailsForXDM(result.addon_details);
  }

  const ages = result.search_context?.pax?.details?.child?.ages;
  if (Array.isArray(ages)) {
    result.search_context = {
      ...result.search_context,
      pax: {
        ...result.search_context.pax,
        details: {
          ...result.search_context.pax.details,
          child: {
            ...result.search_context.pax.details.child,
            ages: ages.length > 0 ? ages.map((age: any) => String(age)) : ['NA'],
          },
        },
      },
    };
  }

  if (Array.isArray(result.traveller_info)) {
    result.traveller_info = result.traveller_info.map((traveller: any) => ({
      ...traveller,
      age: traveller.age !== undefined && traveller.age !== null
        ? Number(traveller.age) || 0
        : 0,
    }));
  }

  const productList = result.event_detail?.components?.product_list;
  if (Array.isArray(productList)) {
    result.event_detail = {
      ...result.event_detail,
      components: {
        ...result.event_detail.components,
        product_list: productList.map((product: any) => ({
          ...product,
          ...(product.duration !== undefined && { duration: parseDurationToMinutes(product.duration) }),
          ...(Array.isArray(product.legs) && {
            legs: product.legs.map((leg: any) => ({
              ...leg,
              ...(leg.duration !== undefined && { duration: parseDurationToMinutes(leg.duration) }),
            })),
          }),
        })),
      },
    };
  }

  return result;
};

export const buildSearchContextFromState = (state: any) => {
  const { originStation, destinationStation, departureDate } = state?.railsListing || {};
  const locusDetails = state?.railsLanding?.LocusDetails || {};
  const selectedTravelers = state?.railsTraveler?.selectedTravelers || [];

  return {
    originStation: enrichStation(originStation, {
      locusCode: locusDetails?.fromLocusCode,
      locusType: locusDetails?.sourceLocusType,
      locusV2Id: locusDetails?.sourceLocusV2Id,
      locusV2Type: locusDetails?.sourceLocusV2Type,
    }),
    destinationStation: enrichStation(destinationStation, {
      locusCode: locusDetails?.toLocusCode,
      locusType: locusDetails?.destinationLocusType,
      locusV2Id: locusDetails?.destinationLocusV2Id,
      locusV2Type: locusDetails?.destinationLocusV2Type,
    }),
    departureDate,
    pax: {
      adult: Math.max(selectedTravelers.length, 1),
      child: { count: 0, ages: [] },
      infant: 0,
    },
  };
};
